import { Controller, useFormContext } from "react-hook-form";
import { ApplicationQuestion } from "@/pages/_layouts/jobs/types";
import TextField from "@/components/TextField";
import Select from "@/components/Select";
import Checkbox from "@/components/Checkbox";
import { QuestionType } from "@/components/AssessmentQuestion/types";
import { CreateJobApplicationFormData } from "./schema";

interface DynamicQuestionRendererProps {
  questions: ApplicationQuestion[];
}

export default function DynamicQuestionRenderer({ questions }: DynamicQuestionRendererProps) {
  const { control } = useFormContext<CreateJobApplicationFormData>();

  if (!questions || questions.length === 0) {
    return null;
  }

  const sortedQuestions = [...questions].sort((a, b) => (a.position || 0) - (b.position || 0));

  return (
    <div className="space-y-4">
      <div className="text-title-medium mb-4">Additional Questions</div>
      {sortedQuestions.map((question, index) => (
        <div className="flex flex-col gap-2" key={question.id || index}>
          {question.questionType === QuestionType.OPEN_TEXT && (
            <Controller
              name={`customAnswers.${index}`}
              control={control}
              defaultValue={{
                questionId: question.id!,
                questionType: question.questionType,
                textAnswer: "",
                selectedOptionId: "",
                selectedOptionIds: []
              }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  label={question.content}
                  value={field.value?.textAnswer || ""}
                  onChangeText={(value) =>
                    field.onChange({
                      ...field.value,
                      textAnswer: value
                    })
                  }
                  required
                  type="textarea"
                  error={!!error}
                  errorText={error?.message}
                  rows={3}
                />
              )}
            />
          )}

          {(question.questionType === QuestionType.SINGLE_SELECT ||
            question.questionType === QuestionType.MULTI_CHOICE) && (
            <Controller
              name={`customAnswers.${index}`}
              control={control}
              defaultValue={{
                questionId: question.id!,
                questionType: question.questionType,
                textAnswer: "",
                selectedOptionId: "",
                selectedOptionIds: []
              }}
              render={({ field, fieldState: { error } }) => (
                <Select
                  label={question.content}
                  value={field.value?.selectedOptionId || ""}
                  onSelect={(value) =>
                    field.onChange({
                      ...field.value,
                      selectedOptionId: value
                    })
                  }
                  options={(question.options || []).map((opt) => ({
                    value: opt.id!,
                    label: opt.content
                  }))}
                  required
                  error={!!error}
                  errorText={error?.message}
                />
              )}
            />
          )}

          {question.questionType === QuestionType.MULTI_SELECT && (
            <Controller
              name={`customAnswers.${index}`}
              control={control}
              defaultValue={{
                questionId: question.id!,
                questionType: question.questionType,
                textAnswer: "",
                selectedOptionId: "",
                selectedOptionIds: []
              }}
              render={({ field, fieldState: { error } }) => (
                <div>
                  <label
                    className={`text-body-large mb-2 block ${
                      error ? "text-sys-brand-error" : "text-sys-brand-on-surface"
                    }`}
                  >
                    {question.content}
                    <span className="text-sys-brand-error ml-1">*</span>
                  </label>
                  <div className="space-y-2">
                    {(question.options || []).map((option) => {
                      const isChecked = field.value?.selectedOptionIds?.includes(option.id!) || false;
                      return (
                        <Checkbox
                          key={option.id}
                          name={`custom-answer-${question.id}-${option.id}`}
                          label={option.content}
                          checked={isChecked}
                          onChange={(checked) => {
                            const currentIds = field.value?.selectedOptionIds || [];
                            const newIds = checked
                              ? [...currentIds, option.id!]
                              : currentIds.filter((id) => id !== option.id);
                            field.onChange({
                              ...field.value,
                              selectedOptionIds: newIds
                            });
                          }}
                        />
                      );
                    })}
                  </div>
                  {error && <p className="text-sys-brand-error text-body-small mt-1">{error.message}</p>}
                </div>
              )}
            />
          )}
        </div>
      ))}
    </div>
  );
}
